"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Preview = void 0;
const uuid_1 = require("uuid");
class Preview {
    /**
     * Sets up the Creatomate Web SDK plugin. You can choose between 'player' and 'interactive' modes.
     * - The player mode offers a static video player with a play button and time scrubber.
     * - In interactive mode, users can change the content by dragging and dropping, just like in Creatomate's template editor.
     *
     * @param element The HTML DIV element to use as a container. The preview automatically adjusts the size of this element.
     * @param mode Choose 'player' or 'interactive'. When in doubt, choose 'player'.
     * @param publicToken Your project's public token. You can find your token in your Creatomate dashboard under project settings.
     */
    constructor(element, mode, publicToken) {
        this.element = element;
        /**
         * Whether the plugin is ready for use. Do not use any of the functions of this instance prior to the plugin is ready.
         *
         * @see onReady()
         */
        this.ready = false;
        this._pendingPromises = {};
        // Defined as arrow function to make it bound to this instance when used with window.addEventListener above.
        this._handleMessage = (e) => {
            if (!e.data || typeof e.data !== 'object') {
                return;
            }
            // Ignore messages from other sources
            if (this._iframe.contentWindow !== e.source) {
                return;
            }
            const _a = e.data, { id, message, error } = _a, args = __rest(_a, ["id", "message", "error"]);
            if (id) {
                // Resolve pending promise
                const pendingPromise = this._pendingPromises[id];
                if (pendingPromise) {
                    if (error) {
                        pendingPromise.reject(new Error(error));
                    }
                    else {
                        pendingPromise.resolve(args);
                    }
                    // Clean up
                    delete this._pendingPromises[id];
                }
            }
            else {
                switch (message) {
                    case 'onReady':
                        // The component is ready to use
                        this.ready = true;
                        // Set the mode as provided in the constructor
                        this.setMode(this.mode).then();
                        if (this.onReady) {
                            this.onReady();
                        }
                        break;
                    case 'onLoad':
                        if (this.onLoad) {
                            this.onLoad();
                        }
                        break;
                    case 'onLoadComplete':
                        if (this.onLoadComplete) {
                            this.onLoadComplete();
                        }
                        break;
                    case 'onPlay':
                        if (this.onPlay) {
                            this.onPlay();
                        }
                        break;
                    case 'onPause':
                        if (this.onPause) {
                            this.onPause();
                        }
                        break;
                    case 'onTimeChange':
                        if (this.onTimeChange) {
                            this.onTimeChange(args.time);
                        }
                        break;
                    case 'onToolChange':
                        if (this.onToolChange) {
                            this.onToolChange(args.tool);
                        }
                        break;
                    case 'onActiveCompositionChange':
                        if (this.onActiveCompositionChange) {
                            this.onActiveCompositionChange(args.elementId);
                        }
                        break;
                    case 'onActiveElementsChange':
                        if (this.onActiveElementsChange) {
                            this.onActiveElementsChange(args.elementIds);
                        }
                        break;
                    case 'onScaleChange':
                        if (this.onScaleChange) {
                            this.onScaleChange(args.scale);
                        }
                        break;
                    case 'onStateChange':
                        this.state = args.state;
                        if (this.onStateChange) {
                            this.onStateChange(args.state);
                        }
                        break;
                }
            }
        };
        this.mode = mode;
        const iframe = document.createElement('iframe');
        iframe.setAttribute('width', '100%');
        iframe.setAttribute('height', '100%');
        iframe.setAttribute('scrolling', 'no');
        iframe.setAttribute('allow', 'autoplay');
        iframe.setAttribute('src', `https://creatomate.com/embed?version=1.6.0&token=${publicToken}`);
        iframe.style.border = 'none';
        iframe.style.display = 'none';
        element.innerHTML = '';
        element.style.overflow = 'hidden';
        element.append(iframe);
        window.addEventListener('message', this._handleMessage);
        this._iframe = iframe;
    }
    /**
     * Disposes the resources of this plugin. You can call this after you have finished using it.
     */
    dispose() {
        var _a;
        window.removeEventListener('message', this._handleMessage);
        (_a = this._iframe.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(this._iframe);
        this._iframe.setAttribute('src', '');
        this._pendingPromises = {};
    }
    /**
     * Sets the mode. See the constructor for more information about these modes.
     *
     * @param mode 'player' or 'interactive' mode.
     */
    setMode(mode) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._sendCommand({ message: 'setMode', mode }).catch((error) => {
                throw new Error(`Failed to set mode: ${error.message}`);
            });
        });
    }
    /**
     * Displays or hides the player controls. By default, the controls are visible and allow users to pause, play, and navigate the video.
     * Even when the controls are hidden, it is still possible to change playback state through 'play()', 'pause()', 'setTime()', etc.
     * Note that when an image template instead of a video is loaded, the controls are always hidden.
     *
     * @param enabled Set to 'true' to show the controls (default), or set to 'false' to hide the controls.
     * @see play()
     * @see pause()
     * @see setTime()
     */
    setControls(enabled) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._sendCommand({ message: 'setControls', enabled }).catch((error) => {
                throw new Error(`Failed to set controls: ${error.message}`);
            });
        });
    }
    /**
     * Loads a template from your project.
     * Make sure the template is located in the project from which you are using the public token.
     *
     * @param templateId The ID of the template.
     * @param createUndoPoint Set to 'true' if you wish to add an undo point to be used later with 'undo()' and 'redo()'.
     * @see undo()
     * @see redo()
     */
    loadTemplate(templateId, createUndoPoint = false) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._sendCommand({ message: 'setTemplate', templateId, createUndoPoint }).catch((error) => {
                throw new Error(`Failed to load template: ${error.message}`);
            });
            // Show iframe
            this._iframe.style.display = '';
        });
    }
    /**
     * Sets the JSON source of the video or image that is being displayed.
     *
     * @param source The source of the video or image.
     * @param createUndoPoint Set to 'true' if you wish to add an undo point to be used later with 'undo()' and 'redo()'.
     * @see https://creatomate.com/docs/json/introduction
     * @see undo()
     * @see redo()
     */
    setSource(source, createUndoPoint = false) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._sendCommand({ message: 'setSource', source, createUndoPoint }).catch((error) => {
                throw new Error(`Failed to set source: ${error.message}`);
            });
            // Show iframe
            this._iframe.style.display = '';
        });
    }
    /**
     * Gets the source JSON of an element, or the full source of the video/image if no element is provided.
     *
     * @param state Optional. The state from which to extract the source JSON. Defaults to 'this.state'.
     * @return The source JSON.
     * @see https://creatomate.com/docs/json/introduction
     */
    getSource(state = this.state) {
        if (!state) {
            return {};
        }
        else if (state.elements) {
            return Object.assign(Object.assign({}, state.source), { elements: state.elements.map((element) => this.getSource(element)) });
        }
        else {
            return state.source;
        }
    }
    /**
     * Gets all nested elements of an element recursively, or all elements of the video/image if no element is provided.
     *
     * @param state Optional. The state from which to retrieve the elements. Defaults to 'this.state'.
     * @return The elements in an array.
     */
    getElements(state = this.state) {
        const elements = [];
        if (state) {
            if (state.source.type) {
                elements.push(state);
            }
            if (Array.isArray(state.elements)) {
                for (const nestedElement of state.elements) {
                    elements.push(...this.getElements(nestedElement));
                }
            }
        }
        return elements;
    }
    /**
     * Finds an element by a predicate recursively.
     *
     * @param predicate Predicate function that is called for each element until 'true' is returned.
     * @param state Optional. The state to scan for an element. Defaults to 'this.state'.
     * @return The state of the element found, or 'undefined' when the predicate returned 'false' for all elements.
     */
    findElement(predicate, state = this.state) {
        if (state === null || state === void 0 ? void 0 : state.elements) {
            for (const element of state.elements) {
                if (predicate(element)) {
                    return element;
                }
                if ('elements' in element) {
                    const foundNestedElement = this.findElement(predicate, element);
                    if (foundNestedElement) {
                        return foundNestedElement;
                    }
                }
            }
        }
        return undefined;
    }
    /**
     * Sets the modifications that are applied to the video or image source.
     * Unlike 'applyModifications()', this function does not mutate the source set by 'loadTemplate()' or 'setSource()'.
     *
     * When should you use 'setModifications' or 'applyModifications'?
     * - If you use the 'player' mode and wish to replace the content of dynamic elements, 'setModifications()' is probably what you need.
     * - If you use the 'interactive' mode and want to make a definitive change to any element, you'll probably want to use 'applyModifications()' instead.
     *
     * @param modifications A modifications object.
     * @see https://creatomate.com/docs/api/rest-api/the-modifications-object
     * @see applyModifications()
     * @see loadTemplate()
     * @see setSource()
     */
    setModifications(modifications) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this._sendCommand({ message: 'setModifications', modifications }).catch((error) => {
                throw new Error(`Failed to set modifications: ${error.message}`);
            });
        });
    }
    /**
     * Applies modifications to the source. An undo point is created automatically.
     * The difference between this and 'setModifications()' is that this function modifies the source JSON.
     *
     * @param modifications A modifications object.
     * @see https://creatomate.com/docs/api/rest-api/the-modifications-object
     * @see setModifications()
     * @see undo()
     * @see redo()
     */
    applyModifications(modifications) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._sendCommand({ message: 'applyModifications', modifications }).catch((error) => {
                throw new Error(`Failed to apply modifications: ${error.message}`);
            });
        });
    }
    /**
     * Reverts the last changes made by 'loadTemplate', 'setSource()', 'applyModifications()', or the user when in interactive mode.
     *
     * @see setSource()
     * @see applyModifications()
     */
    undo() {
        return __awaiter(this, void 0, void 0, function* () {
            return this._sendCommand({ message: 'undo' }).catch((error) => {
                throw new Error(`Failed to undo: ${error.message}`);
            });
        });
    }
    /**
     * Reapplies the changes from the redo stack. This function won't have any effect if 'undo()' has not been called previously.
     *
     * @see undo()
     */
    redo() {
        return __awaiter(this, void 0, void 0, function* () {
            return this._sendCommand({ message: 'redo' }).catch((error) => {
                throw new Error(`Failed to redo: ${error.message}`);
            });
        });
    }
    /**
     * Clears the undo history stack, preventing undoing or redoing previous changes.
     *
     * @param stack Specify 'undo' or 'redo' to only clear their respective states, or omit this parameter to clear both.
     * @see undo()
     * @see redo()
     */
    clearHistory(stack) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._sendCommand({ message: 'clearHistory', stack }).catch((error) => {
                throw new Error(`Failed to clear the history: ${error.message}`);
            });
        });
    }
    /**
     * Starts playing the video.
     *
     * @see onPlay()
     */
    play() {
        return __awaiter(this, void 0, void 0, function* () {
            return this._sendCommand({ message: 'play' }).catch((error) => {
                throw new Error(`Failed to play: ${error.message}`);
            });
        });
    }
    /**
     * Pauses the video.
     *
     * @see onPause()
     */
    pause() {
        return __awaiter(this, void 0, void 0, function* () {
            return this._sendCommand({ message: 'pause' }).catch((error) => {
                throw new Error(`Failed to pause: ${error.message}`);
            });
        });
    }
    /**
     * Seeks to the provided playback time.
     *
     * @param time Playback time in seconds.
     * @see onTimeChange()
     */
    setTime(time) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._sendCommand({ message: 'setTime', time }).catch((error) => {
                throw new Error(`Failed to set time: ${error.message}`);
            });
        });
    }
    /**
     * This setting controls whether the video starts over once it has reached the end.
     *
     * @param loop Whether or not the video should loop (true by default).
     */
    setLoop(loop) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._sendCommand({ message: 'setLoop', loop }).catch((error) => {
                throw new Error(`Failed to set loop: ${error.message}`);
            });
        });
    }
    /**
     * Sets the current mouse tool.
     *
     * @param tool Any of the available mouse tools; default, pen, text, ellipse, or rectangle.
     * @see onToolChange()
     */
    setTool(tool) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._sendCommand({ message: 'setTool', tool }).catch((error) => {
                throw new Error(`Failed to set tool: ${error.message}`);
            });
        });
    }
    /**
     * Sets the currently active composition when in interactive mode.
     *
     * @param elementId The ID of the composition to make active or 'null' to activate the main composition.
     * @see onActiveCompositionChange()
     */
    setActiveComposition(elementId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._sendCommand({ message: 'setActiveComposition', elementId }).catch((error) => {
                throw new Error(`Failed to set active composition: ${error.message}`);
            });
        });
    }
    /**
     * Sets the currently selected elements when in interactive mode.
     *
     * @param elementIds The IDs of the elements to select.
     * @see onActiveElementsChange()
     */
    setActiveElements(elementIds) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._sendCommand({ message: 'setActiveElements', elementIds }).catch((error) => {
                throw new Error(`Failed to set active elements: ${error.message}`);
            });
        });
    }
    /**
     * Sets the zoom state in interactive mode.
     *
     * Zoom mode can be any of these values:
     * - 'free': Allows the user to freely pan and zoom the canvas. The default option.
     * - 'auto': The canvas is automatically scaled according to the viewport size.
     * - 'fixed': Set the canvas to a fixed scale as provided by the 'scale' parameter.
     * - 'centered': Keeps the canvas in the center when zoomed out.
     *
     * @param mode Zoom mode.
     * @param scale Optional zoom scale (1.0 = 100%).
     */
    setZoom(mode, scale) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._sendCommand({ message: 'setZoom', mode, scale }).catch((error) => {
                throw new Error(`Failed to set the zoom state: ${error.message}`);
            });
        });
    }
    /**
     * Ensures that an asset can be used immediately as a source for a video, image, or audio element by caching it.
     * As a result, the file is immediately available without waiting for the upload to complete.
     *
     * @param url The URL of the file. This URL won't be requested because the Blob should provide the file content already.
     * @param blob The content of the file. Make sure that the file is available at the URL eventually,
     *             as there is no guarantee that it will remain cached.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Blob
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Cache
     */
    cacheAsset(url, blob) {
        return this._sendCommand({ message: 'cacheAsset', url }, { blob }).catch((error) => {
            throw new Error(`Failed to cache asset: ${error.message}`);
        });
    }
    /**
     * Sets a list of RegExp rules used to determine whether a video asset should be fully cached on the client's device.
     * This is especially useful for large video files that take a long time to download.
     * These rules do not apply to files hosted by the Creatomate CDN, because those are always cached.
     *
     * The cache is disabled by default for video assets not hosted by Creatomate's CDN since @creatomate/preview version 1.4.
     * You can still cache the assets with a custom RegExp list passed to this function.
     *
     * @param rules A list of regular expressions matched against every video URL.
     * @example
     * // Disable caching of video files for URLs beginning with https://www.example.com/
     * setCacheBypassRules([ /^https:\/\/www\.example\.com\// ]);
     */
    setCacheBypassRules(rules) {
        const serializedRules = rules.map((rule) => rule.source);
        return this._sendCommand({ message: 'setCacheBypassRules', rules: serializedRules }).catch((error) => {
            throw new Error(`Failed to set cache bypass rules: ${error.message}`);
        });
    }
    _sendCommand(message, payload) {
        var _a;
        if (!this.ready) {
            throw new Error('The SDK is not yet ready. Please wait for the onReady event before calling any methods.');
        }
        const id = (0, uuid_1.v4)();
        (_a = this._iframe.contentWindow) === null || _a === void 0 ? void 0 : _a.postMessage(Object.assign(Object.assign({ id }, JSON.parse(JSON.stringify(message))), payload), '*');
        // Create pending promise
        return new Promise((resolve, reject) => (this._pendingPromises[id] = { resolve, reject }));
    }
}
exports.Preview = Preview;
